import React, { Suspense, lazy, useCallback, useContext, useMemo, useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderTemplate from "./api";
import { loadTemplateDependencies } from "./features/template.dependencies";
import { templateFilterFields } from "./features/template.filter";
import { getTemplateColumns } from "./template-list/templateColumns";
import TemplateRowActions from "./template-list/TemplateRowActions";
import TemplateViewDrawer from "./template-dialogs/TemplateViewDrawer";

// The editor is only needed once opened.
const TemplateEditor = lazy(() => import("./template-editor/TemplateEditor"));

// Reminder Library -> Template (tbl_rem_template): what a reminder says, per channel.
const Template = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderTemplate.getAll, { loadError: "Failed to load templates.", initialSort: { key: "id", direction: "desc" } });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [editor, setEditor] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [busy, setBusy] = useState(null);

  /** Runs a row request with its busy spinner. */
  const withBusy = async (row, action, run) => {
    setBusy({ id: row.id, action });
    try {
      await run();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err) });
    } finally {
      setBusy(null);
    }
  };

  const openView = (row) =>
    withBusy(row, "view", async () => {
      const res = await apiReminderTemplate.getById(row.id);
      setViewRecord(res.data?.data ?? null);
    });

  const handleClone = (row) =>
    withBusy(row, "clone", async () => {
      const res = await apiReminderTemplate.clone(row.id);
      showAlert({ type: "success", message: res.data?.message || "Copied." });
      list.reload();
    });

  const deleteTemplates = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Template",
      message: `Delete ${ids.length > 1 ? `${ids.length} templates` : "this template"}? Templates used by a reminder rule step can't be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderTemplate.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const { reload } = list;
  const closeEditor = useCallback(
    (changed) => {
      setEditor(null);
      if (changed) reload();
    },
    [reload]
  );

  const columns = useMemo(() => getTemplateColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <TemplateRowActions
      row={row}
      canManage={canManage}
      busy={busy}
      onView={openView}
      onEdit={(target) => setEditor({ id: target.id })}
      onClone={handleClone}
      onDelete={(target) => deleteTemplates([target.id])}
    />
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setEditor({ id: null })}
          onDeleteSelected={canManage ? () => deleteTemplates(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={templateFilterFields} dependenciesLoader={loadTemplateDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={FileText}
              title={list.hasQuery ? "No templates match" : "No reminder templates yet"}
              message={
                list.hasQuery
                  ? "No templates match your search or filters. Try adjusting them."
                  : "A template is the message a reminder sends on one channel, with placeholders such as {{row.title}} filled in per record."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Create Template"
              onCreate={() => setEditor({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <Suspense fallback={null}>{editor && <TemplateEditor templateId={editor.id} onClose={closeEditor} />}</Suspense>

      {viewRecord && <TemplateViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default Template;
